import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Paperclip, X, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/Shared";
import { api, errMsg, todayISO, EXPENSE_GROUPS, groupOf } from "@/lib/api";

const empty = () => ({ date: todayISO(), amount: "", category: "Fuel", description: "", receipt_id: null, receipt_name: null });

const ReceiptPicker = ({ form, setForm }) => {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("Receipt file is too large (max 10 MB)");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/receipts", fd);
      setForm((f) => ({ ...f, receipt_id: data.id, receipt_name: data.filename }));
      toast.success("Receipt attached");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  if (form.receipt_id)
    return (
      <div className="flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 h-12" data-testid="expense-receipt-attached">
        <span className="flex items-center gap-2 text-sm font-semibold text-emerald-900 truncate">
          <Paperclip className="w-4 h-4 shrink-0" />
          <span className="truncate">{form.receipt_name}</span>
        </span>
        <button type="button" onClick={() => setForm((f) => ({ ...f, receipt_id: null, receipt_name: null }))} className="p-1 rounded-md hover:bg-emerald-100" data-testid="expense-receipt-remove">
          <X className="w-4 h-4 text-emerald-900" />
        </button>
      </div>
    );

  return (
    <>
      <input ref={ref} type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} data-testid="expense-receipt-input" />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={uploading}
        className="w-full h-12 rounded-xl border-2 border-dashed border-slate-300 text-slate-600 font-semibold flex items-center justify-center gap-2 hover:border-emerald-500 hover:text-emerald-800 transition-colors"
        data-testid="expense-receipt-upload-button"
      >
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {uploading ? "Uploading..." : "Upload receipt photo or PDF"}
      </button>
    </>
  );
};

export const ExpenseDialog = ({ open, onOpenChange, expense, onSaved }) => {
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(expense ? { ...empty(), ...expense } : empty());
  }, [open, expense]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { date: form.date, amount: parseFloat(form.amount), category: form.category, description: form.description, receipt_id: form.receipt_id };
      if (expense) await api.put(`/expenses/${expense.id}`, payload);
      else await api.post("/expenses", payload);
      toast.success(expense ? "Expense updated" : "Expense saved");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl p-0 max-h-[92vh] overflow-y-auto" data-testid="expense-dialog">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="font-heading text-2xl font-bold">{expense ? "Edit Expense" : "Add Expense"}</DialogTitle>
          <DialogDescription>Record a business expense and attach the receipt if you have it.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="px-6 pb-6 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date" htmlFor="exp-date">
              <Input id="exp-date" type="date" required value={form.date} onChange={set("date")} className="h-12 text-base" data-testid="expense-date-input" />
            </Field>
            <Field label="Amount ($)" htmlFor="exp-amount">
              <Input id="exp-amount" type="number" inputMode="decimal" step="0.01" min="0.01" required placeholder="0.00" value={form.amount} onChange={set("amount")} className="h-12 text-base font-mono" data-testid="expense-amount-input" />
            </Field>
          </div>
          <Field label="Category">
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <SelectTrigger className="h-12 text-base" data-testid="expense-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-80">
                {EXPENSE_GROUPS.map((g) => (
                  <SelectGroup key={g.key}>
                    <SelectLabel className="text-xs uppercase tracking-wider text-slate-500">{g.label}</SelectLabel>
                    {g.categories.map((c) => (
                      <SelectItem key={c} value={c} className="h-10 text-base" data-testid={`expense-category-option-${c.toLowerCase()}`}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-2" data-testid="expense-classification-note">
              <strong>{groupOf(form.category).label}:</strong> {groupOf(form.category).note}
            </p>
          </Field>
          <Field label="Description" htmlFor="exp-desc">
            <Textarea id="exp-desc" rows={2} placeholder="e.g. Diesel fill-up at Love's" value={form.description} onChange={set("description")} className="text-base" data-testid="expense-description-input" />
          </Field>
          <Field label="Receipt (optional)">
            <ReceiptPicker form={form} setForm={setForm} />
          </Field>
          <button type="submit" disabled={saving} className="mt-btn-dark w-full" data-testid="expense-form-submit">
            {saving && <Loader2 className="w-5 h-5 animate-spin" />}
            {expense ? "Save Changes" : "Save Expense"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
